import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_CUSTOMER_ACCOUNT_ROUTER } from "../../../services/api-config";

/**
 * Tài khoản đăng nhập app khách hàng.
 *
 * Gọi sang USER_SERVICE (không phải CUSTOMER_SERVICE): hồ sơ khách và tài khoản đăng nhập
 * là hai thứ khác nhau, nằm ở hai service khác nhau, nối với nhau bằng `customer_id`.
 *
 * Khách KHÔNG tự đăng ký — nhân viên cấp tài khoản tại đây.
 */

export interface IssueCustomerAccountPayload {
  /** ID hồ sơ khách bên customer service. */
  customer_id: number;
  /** Tên đăng nhập: chữ thường, số và các ký tự . _ - ; tối thiểu 4 ký tự. */
  username: string;
  /** Bỏ trống để hệ thống tự sinh mật khẩu tạm. */
  password?: string;
  /** Chép từ hồ sơ khách để app hiển thị nhanh, không phải nguồn sự thật. */
  name: string;
  phone: string;
}

export interface CustomerAccountResult {
  id: string;
  username: string;
  customer_id: string;
  name: string;
  phone: string;
  /**
   * Mật khẩu tạm dạng chữ thường.
   *
   * CHỈ có đúng một lần trong phản hồi này — trong DB chỉ còn hash, không lấy lại được.
   * Giao diện phải cho nhân viên chép trước khi đóng cửa sổ.
   */
  password: string;
}

/** Tóm tắt tài khoản, đủ để biết dòng khách nào hiện nút gì. */
export interface CustomerAccountSummary {
  id: string;
  customer_id: string;
  username: string;
  /** 1 = đang hoạt động, 0 = đã khoá. */
  status: number;
  /** 1 = khách chưa đổi mật khẩu tạm lần nào. */
  must_change_password: number;
}

export const CustomerAccountService = {
  /**
   * Tra một lượt xem khách nào trong trang hiện tại đã có tài khoản app.
   *
   * Chỉ trả về khách CÓ tài khoản — khách không có trong kết quả nghĩa là chưa được cấp.
   *
   * Ghép ở phía trình duyệt thay vì để customer service gọi chéo sang user service:
   * giữ hai service độc lập, và user service có trục trặc thì danh sách khách vẫn hiện
   * đầy đủ, chỉ mất phần trạng thái tài khoản.
   */
  lookupByCustomers: () => {
    const { request, baseResponse, loading } = useFetchData.Post<CustomerAccountSummary[]>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });

    return {
      fetch: (customerIds: number[]) =>
        request({
          endPoint: API_CUSTOMER_ACCOUNT_ROUTER.API_LOOKUP_BY_CUSTOMERS,
          data: { customer_ids: customerIds },
        }),
      response: baseResponse,
      loading,
    };
  },

  /** Cấp tài khoản app cho một khách đã có hồ sơ. Cần quyền CRM_ADVANCED. */
  issue: () => {
    const { request, baseResponse, loading } = useFetchData.Post<CustomerAccountResult>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });

    return {
      fetch: (payload: IssueCustomerAccountPayload) =>
        request({
          endPoint: API_CUSTOMER_ACCOUNT_ROUTER.API_ISSUE,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  /**
   * Đặt lại mật khẩu khi khách quên (kế hoạch mục 1.4: khách gọi CSKH, nhân viên cấp lại).
   * Thao tác này đá luôn phiên khách đang đăng nhập và bắt khách đổi mật khẩu ở lần vào sau.
   */
  resetPassword: () => {
    const { request, baseResponse, loading } = useFetchData.Post<CustomerAccountResult>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });

    return {
      fetch: (accountId: number, password?: string) =>
        request({
          endPoint: API_CUSTOMER_ACCOUNT_ROUTER.API_RESET_PASSWORD(accountId),
          data: password ? { password } : {},
        }),
      response: baseResponse,
      loading,
    };
  },
};
